// useFeatureWindows — the generic feature-window state machine, generalized
// from the JAWS workbench's `openPanel` (which single feature is open) +
// `poppedOut` (which features the user detached to their own OS window) state
// in SuspensionKinematics.tsx.
//
// Keys are opaque strings (the feature-item keys from featureWindowRegistry).
// One window is open at a time (`openKey`), matching JAWS' single-panel model;
// any number can be popped out. Popping a window out doesn't change which is
// "open" — a popped window renders in its own OS window while `openKey` may
// point elsewhere (same as JAWS, where switching the in-viewer panel leaves a
// popped panel up).
//
// Boundary note: the normal Feature Tree (FeatureTreeSidebar) and the viewport
// host (ParametricPreviewSection) live in different subtrees with no shared
// EDITABLE React-provider ancestor in this checkout. Rather than widen the
// already-too-wide HomepageContext or edit Layout/LayoutContent to mount a
// provider, this hook is backed by a tiny module-level store subscribed via
// useSyncExternalStore. Every consumer sees the same state, and the "context"
// is just this hook — no extra provider needed. This is the permanent,
// default-ON path for every domain's feature tree — gated behind the
// `feature-tree-windows` flag (lib/featureFlags.ts) purely as a fast
// kill-switch, not as an in-progress pilot.

import { useCallback, useSyncExternalStore } from 'react';

export interface FeatureWindowsState {
  /** Which single feature window is open in the viewer (null = none). */
  openKey: string | null;
  /** Feature keys the user popped out into their own OS window. */
  poppedOut: Set<string>;
}

export interface FeatureWindowsApi extends FeatureWindowsState {
  /** Open a feature window (closes whichever was open). */
  openFeature: (key: string) => void;
  /** Close the currently-open window (or a specific one). */
  closeFeature: (key?: string) => void;
  /** Pop a feature out to its own OS window (true) or dock it back (false). */
  popOut: (key: string, out: boolean) => void;
  /** Toggle a feature's popped-out state. */
  togglePopOut: (key: string) => void;
}

// ---------------------------------------------------------------------------
// Module-level store (see boundary note above).
// ---------------------------------------------------------------------------

let state: FeatureWindowsState = { openKey: null, poppedOut: new Set() };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function setState(next: FeatureWindowsState) {
  state = next;
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

// Stable action identities — defined once at module scope so consumers get the
// same function references on every render (no effect churn).
function openFeature(key: string) {
  if (state.openKey === key) return;
  setState({ ...state, openKey: key });
}

function closeFeature(key?: string) {
  if (key != null && state.openKey !== key) return;
  if (state.openKey === null) return;
  setState({ ...state, openKey: null });
}

function popOut(key: string, out: boolean) {
  const has = state.poppedOut.has(key);
  if (out === has) return;
  const poppedOut = new Set(state.poppedOut);
  if (out) poppedOut.add(key);
  else poppedOut.delete(key);
  setState({ ...state, poppedOut });
}

function togglePopOut(key: string) {
  popOut(key, !state.poppedOut.has(key));
}

/**
 * Read + drive the shared feature-window state. Safe to call from anywhere in
 * the tree — every caller subscribes to the same store.
 */
export function useFeatureWindows(): FeatureWindowsApi {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return {
    openKey: snapshot.openKey,
    poppedOut: snapshot.poppedOut,
    // Wrap in useCallback so the returned object's action identities are stable
    // (the underlying fns already are — this just keeps referential equality
    // predictable for consumers that spread the api into deps).
    openFeature: useCallback(openFeature, []),
    closeFeature: useCallback(closeFeature, []),
    popOut: useCallback(popOut, []),
    togglePopOut: useCallback(togglePopOut, []),
  };
}

/**
 * Reset all feature-window state. Used when the active domain/sub-tab changes
 * so a window left open under one feature tree doesn't linger under another.
 * Callable outside React (e.g. from TopToolbar's select handler).
 */
export function resetFeatureWindows() {
  if (state.openKey === null && state.poppedOut.size === 0) return;
  setState({ openKey: null, poppedOut: new Set() });
}
